<template>
  <div class="follow-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>跟进管理</span>
          <div>
            <el-button type="success" @click="handleExport" :loading="exportLoading">导出跟进</el-button>
            <el-button type="primary" @click="openCreateDialog">新增跟进</el-button>
          </div>
        </div>
      </template>

      <!-- 搜索表单 -->
      <el-form :model="queryParams" class="search-form" label-width="80px">
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item label="客户">
              <el-input
                v-model="queryParams.customerName"
                placeholder="请输入客户姓名"
                clearable
                style="width: 100%"
                @keyup.enter="handleSearch"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="跟进类型">
              <el-select
                v-model="queryParams.activityType"
                placeholder="请选择跟进类型"
                clearable
                style="width: 100%"
                popper-class="follow-type-select-dropdown"
              >
                <el-option label="拜访" value="visit" />
                <el-option label="电话" value="phone" />
                <el-option label="微信" value="wechat" />
                <el-option label="邮件" value="email" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="跟进内容">
              <el-input
                v-model="queryParams.activityContent"
                placeholder="请输入跟进内容"
                clearable
                style="width: 100%"
                @keyup.enter="handleSearch"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label=" ">
              <div class="action-buttons-inline">
                <el-button type="primary" @click="handleSearch">查询</el-button>
                <el-button @click="handleReset">重置</el-button>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="跟进日期">
              <el-date-picker
                v-model="dateRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="下次跟进日期" class="next-follow-time-item">
              <el-date-picker
                v-model="nextFollowDateRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                class="next-follow-date-picker"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <!-- 跟进列表 -->
      <el-table 
        v-loading="loading" 
        :data="followList" 
        border 
        stripe 
        style="width: 100%" 
        empty-text="暂无跟进记录"
        :key="`follow-table-${followList.length}-${total}`"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="customerName" label="客户姓名" width="150" show-overflow-tooltip />
        <el-table-column prop="activityType" label="跟进类型" width="120" align="center">
          <template #default="{ row }">
            <el-tag :type="getActivityTypeTag(row.activityType)" size="small">
              {{ getActivityTypeName(row.activityType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="activityContent" label="跟进内容" min-width="180" show-overflow-tooltip />
        <el-table-column prop="followAddress" label="跟进地址" min-width="280" show-overflow-tooltip />
        <el-table-column prop="activityTime" label="跟进日期" width="180">
          <template #default="{ row }">
            {{ formatDateTime(row.activityTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="nextFollowTime" label="下次跟进日期" width="180">
          <template #default="{ row }">
            {{ row.nextFollowTime ? formatDateTime(row.nextFollowTime) : '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleView(row)">详情</el-button>
            <el-button type="warning" size="small" @click="handleEdit(row)">编辑</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.page"
          v-model:page-size="queryParams.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 查看跟进详情弹窗 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="跟进详情"
      width="700px"
      destroy-on-close
    >
      <el-skeleton v-if="detailLoading" :rows="6" animated />
      <div v-else-if="detailInfo" class="follow-detail">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="跟进客户">
            {{ detailInfo.customerName || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="跟进类型">
            <el-tag :type="getActivityTypeTag(detailInfo.activityType)" size="small">
              {{ getActivityTypeName(detailInfo.activityType) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="跟进日期">
            {{ detailInfo.activityTimeFormatted || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="下次跟进日期">
            {{ detailInfo.nextFollowTimeFormatted || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="位置/地址">
            {{ detailInfo.followAddress || '未填写' }}
          </el-descriptions-item>
          <el-descriptions-item label="跟进内容">
            <div class="detail-content">
              {{ detailInfo.activityContent || '-' }}
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="图片">
            <ImagePreview v-if="detailInfo.images && detailInfo.images.length" :model-value="detailInfo.images" />
            <span v-else>无图片</span>
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>

    <!-- 新增/编辑跟进弹窗 -->
    <el-dialog
      v-model="editDialogVisible"
      :title="isEdit ? '编辑跟进' : '新增跟进'"
      width="700px"
      destroy-on-close
    >
      <el-form
        ref="followFormRef"
        :model="followForm"
        :rules="followFormRules"
        label-width="100px"
        class="follow-form"
      >
        <el-form-item label="跟进客户" prop="customerId">
          <el-select
            v-model="followForm.customerId"
            filterable
            remote
            reserve-keyword
            placeholder="请选择客户"
            :remote-method="loadCustomerOptions"
            :loading="customerLoading"
            style="width: 100%"
            @change="onCustomerChange"
          >
            <el-option
              v-for="item in customerOptions"
              :key="item.id"
              :label="item.customerName"
              :value="item.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="跟进类型" prop="activityType">
          <el-radio-group v-model="followForm.activityType">
            <el-radio label="visit">拜访</el-radio>
            <el-radio label="phone">电话</el-radio>
            <el-radio label="wechat">微信</el-radio>
            <el-radio label="email">邮件</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="跟进内容" prop="activityContent">
          <el-input
            v-model="followForm.activityContent"
            type="textarea"
            :rows="4"
            maxlength="1000"
            show-word-limit
            placeholder="请输入本次跟进的主要内容与结果"
          />
        </el-form-item>

        <el-form-item label="下次跟进日期" prop="nextFollowTime" class="no-wrap-label">
          <el-date-picker
            v-model="followForm.nextFollowTime"
            type="date"
            placeholder="请选择下次跟进日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 100%"
          />
        </el-form-item>

        <el-form-item label="位置/地址" v-if="followForm.activityType === 'visit'">
          <div class="location-input-wrapper">
            <el-input
              v-model="followForm.followAddress"
              placeholder="可填写本次跟进的大致位置或客户地址"
              clearable
            >
              <template #append>
                <el-button :icon="Location" @click="openLocationPicker" :loading="locationLoading">
                  定位
                </el-button>
              </template>
            </el-input>
            <div v-if="followForm.latitude && followForm.longitude" class="location-info">
              <el-tag size="small" type="success">已定位</el-tag>
              <span class="location-coords">
                ({{ followForm.latitude.toFixed(6) }}, {{ followForm.longitude.toFixed(6) }})
              </span>
            </div>
          </div>
        </el-form-item>

        <el-form-item label="图片">
          <ImageUpload 
            v-model="followForm.images"
            :max-count="10"
            :business-type="'FOLLOW'"
            :module="'客户跟进'"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="editDialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="submitLoading" @click="submitFollow">
            保存
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 地图选择器弹窗 -->
    <el-dialog
      v-model="locationDialogVisible"
      title="选择位置"
      width="900px"
      destroy-on-close
      @close="handleLocationDialogClose"
    >
      <div class="location-picker">
        <!-- 搜索框 -->
        <div class="location-search">
          <el-input
            v-model="locationSearchKeyword"
            placeholder="搜索地点"
            clearable
            @keyup.enter="handleLocationSearch"
          >
            <template #append>
              <el-button :icon="Search" @click="handleLocationSearch" :loading="locationSearchLoading">
                搜索
              </el-button>
            </template>
          </el-input>
        </div>

        <!-- 搜索结果列表 -->
        <div v-if="locationSearchResults.length > 0" class="location-results">
          <div
            v-for="(item, index) in locationSearchResults"
            :key="index"
            class="location-result-item"
            @click="selectLocationResult(item)"
          >
            <div class="result-name">{{ item.name }}</div>
            <div class="result-address">{{ item.fullAddress }}</div>
          </div>
        </div>

        <!-- 地图容器 -->
        <div class="location-map-container">
          <div id="location-map" class="location-map">
            <div class="map-placeholder">
              <div class="map-placeholder-text">地图加载中...</div>
              <div class="map-placeholder-tip">提示：请使用搜索功能或定位按钮选择位置</div>
            </div>
          </div>
          <div class="map-tip">地图数据来源：天地图</div>
        </div>

        <!-- 当前选择的位置信息 -->
        <div v-if="selectedLocation" class="selected-location-info">
          <el-descriptions :column="1" border size="small">
            <el-descriptions-item label="地址">
              {{ selectedLocation.address || '未获取到地址' }}
            </el-descriptions-item>
            <el-descriptions-item label="经纬度">
              {{ selectedLocation.latitude.toFixed(6) }}, {{ selectedLocation.longitude.toFixed(6) }}
            </el-descriptions-item>
          </el-descriptions>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="locationDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmLocation" :disabled="!selectedLocation">
            确定
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, nextTick, onBeforeUnmount } from 'vue'
import { ElMessage } from 'element-plus'
import { Location, Search } from '@element-plus/icons-vue'
// eslint-disable-next-line no-unused-vars
import { getFollowList, getFollowDetail, addFollow, updateFollow, getCustomerList, exportFollowList } from '../api/salesman'
import { getCurrentLocation, reverseGeocode, searchLocation } from '../utils/tianditu'
import { batchUploadFiles } from '../utils/fileUtils'
import ImageUpload from '../components/ImageUpload.vue'
import ImagePreview from '../components/ImagePreview.vue'
import { checkQuotaWithLocalFallback, QUOTA_TYPE } from '../utils/quotaCheck'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const loading = ref(false)
const followList = ref([])
const total = ref(0)
const exportLoading = ref(false)
// 上次导出时间，用于限流
const lastExportTime = ref(0)
// 导出限流间隔时间（毫秒），3秒内不允许重复导出
const EXPORT_THROTTLE_INTERVAL = 3000
const queryParams = reactive({
  customerId: null,
  customerName: '',
  activityType: '',
  activityContent: '',
  startDate: '',
  endDate: '',
  nextFollowStartDate: '',
  nextFollowEndDate: '',
  page: 1,
  pageSize: 10
})

// 跟进时间日期范围（用于日期选择器）
const dateRange = ref([])
// 下次跟进时间日期范围（用于日期选择器）
const nextFollowDateRange = ref([])

// 编辑弹窗相关
const editDialogVisible = ref(false)
const isEdit = ref(false)
const submitLoading = ref(false)
const followFormRef = ref(null)

// 查看详情相关
const detailDialogVisible = ref(false)
const detailLoading = ref(false)
const detailInfo = ref(null)

const followForm = reactive({
  id: null,
  customerId: null,
  customerName: '',
  activityType: 'visit',
  activityContent: '',
  nextFollowTime: '',
  followAddress: '',
  latitude: null,
  longitude: null,
  images: []
})

const followFormRules = {
  customerId: [{ required: true, message: '请选择跟进客户', trigger: 'change' }],
  activityType: [{ required: true, message: '请选择跟进类型', trigger: 'change' }],
  activityContent: [{ required: true, message: '请输入跟进内容', trigger: 'blur' }],
  nextFollowTime: [{ required: true, message: '请选择下次跟进日期', trigger: 'change' }]
}

// 客户下拉选择
const customerOptions = ref([])
const customerLoading = ref(false)

// 定位相关
const locationLoading = ref(false)
const locationDialogVisible = ref(false)
const locationSearchKeyword = ref('')
const locationSearchLoading = ref(false)
const locationSearchResults = ref([])
const selectedLocation = ref(null)
let locationMap = null
let locationMarker = null

// 地图选点限流相关
const lastMapClickTime = ref(0)
const firstThrottledClickTime = ref(0) // 首次被限流的点击时间
const MAP_CLICK_THROTTLE_INTERVAL = 1000 // 地图点击限流间隔（毫秒），1秒内不允许重复点击
const MAP_CLICK_COOLDOWN_SECONDS = 5 // 冷却时间（秒），提示用户等待的秒数
let searchDebounceTimer = null // 搜索防抖定时器
const SEARCH_DEBOUNCE_DELAY = 500 // 搜索防抖延迟（毫秒）

// 提交加载状态
const submitting = ref(false)

const formatDateTime = (dateTime) => {
  if (!dateTime) return '-'
  const date = new Date(dateTime)
  if (Number.isNaN(date.getTime())) return '-'
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getActivityTypeName = (type) => {
  const map = {
    visit: '拜访',
    phone: '电话',
    wechat: '微信',
    email: '邮件',
    meeting: '会议',
    other: '其他'
  }
  return map[type] || type || '-'
}

const getActivityTypeTag = (type) => {
  const map = {
    visit: 'success',
    phone: 'primary',
    wechat: 'warning',
    email: 'info',
    meeting: 'danger',
    other: 'info'
  }
  return map[type] || 'info'
}

const loadFollowList = async () => {
  loading.value = true
  try {
    const res = await getFollowList(queryParams)
    if (res.code === '000000') {
      followList.value = res.data?.records || res.data?.list || []
      total.value = res.data?.total || 0
      // 确保表格能正确显示空状态，使用 nextTick 强制更新
      await nextTick()
    } else {
      followList.value = []
      total.value = 0
    }
  } catch (error) {
    console.error('获取跟进列表失败:', error)
    ElMessage.error('获取跟进列表失败')
    followList.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}


const handleSearch = () => {
  // 处理跟进时间日期范围
  if (dateRange.value && dateRange.value.length === 2) {
    queryParams.startDate = dateRange.value[0]
    queryParams.endDate = dateRange.value[1]
  } else {
    queryParams.startDate = ''
    queryParams.endDate = ''
  }
  
  // 处理下次跟进时间日期范围
  if (nextFollowDateRange.value && nextFollowDateRange.value.length === 2) {
    queryParams.nextFollowStartDate = nextFollowDateRange.value[0]
    queryParams.nextFollowEndDate = nextFollowDateRange.value[1]
  } else {
    queryParams.nextFollowStartDate = ''
    queryParams.nextFollowEndDate = ''
  }
  
  queryParams.page = 1
  loadFollowList()
}

const handleReset = () => {
  queryParams.customerId = null
  queryParams.customerName = ''
  queryParams.activityType = ''
  queryParams.activityContent = ''
  queryParams.startDate = ''
  queryParams.endDate = ''
  queryParams.nextFollowStartDate = ''
  queryParams.nextFollowEndDate = ''
  queryParams.page = 1
  dateRange.value = []
  nextFollowDateRange.value = []
  loadFollowList()
}

// 打开新增弹窗
const openCreateDialog = async () => {
  // 先检查跟进额度
  const result = await checkQuotaWithLocalFallback(QUOTA_TYPE.FOLLOW, {
    allowLocalOperation: false,
    operationName: '新增跟进'
  })
  
  if (!result.allowed) {
    // 额度已超限，不允许新增
    return
  }
  
  isEdit.value = false
  Object.assign(followForm, {
    id: null,
    customerId: null,
    customerName: '',
    activityType: 'visit',
    activityContent: '',
    nextFollowTime: '',
    followAddress: '',
    latitude: null,
    longitude: null,
    images: []
  })
  editDialogVisible.value = true
}

// 加载客户选项（远程搜索）
const loadCustomerOptions = async (keyword = '') => {
  customerLoading.value = true
  try {
    const res = await getCustomerList({
      page: 1,
      pageSize: 20,
      customerName: keyword || undefined
    })
    const data = res.data || {}
    customerOptions.value = Array.isArray(data.list) ? data.list : []
  } catch (error) {
    console.error('获取客户列表失败:', error)
  } finally {
    customerLoading.value = false
  }
}

const onCustomerChange = (val) => {
  const target = customerOptions.value.find(item => item.id === val)
  followForm.customerName = target ? target.customerName : ''
}

// 查看详情
const handleView = async (row) => {
  detailDialogVisible.value = true
  detailLoading.value = true
  detailInfo.value = null

  try {
    const res = await getFollowDetail(row.id)
    if (res.code !== '000000' || !res.data) {
      throw new Error(res.msg || '获取跟进详情失败')
    }
    const data = res.data

    // 解析图片字段（后端存的是 JSON 字符串）
    let images = []
    if (data.images) {
      try {
        const parsed = JSON.parse(data.images)
        if (Array.isArray(parsed)) {
          images = parsed
        } else if (typeof parsed === 'string') {
          images = [parsed]
        }
      } catch {
        if (typeof data.images === 'string') {
          images = data.images.split(',').map(s => s.trim()).filter(Boolean)
        }
      }
    }

    // 确保图片 URL 为完整地址
    const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
    images = images.map(img => {
      if (!img) return ''
      if (img.startsWith('http://') || img.startsWith('https://')) return img
      return img.startsWith('/') ? baseURL + img : `${baseURL}/${img}`
    }).filter(Boolean)

    detailInfo.value = {
      id: data.id,
      customerName: data.customerName || row.customerName || '',
      activityType: data.activityType || row.activityType || 'visit',
      activityContent: data.activityContent || '',
      activityTime: data.activityTime || row.activityTime || null,
      activityTimeFormatted: data.activityTime ? formatDateTime(data.activityTime) : (row.activityTime ? formatDateTime(row.activityTime) : ''),
      nextFollowTime: data.nextFollowTime || row.nextFollowTime || null,
      nextFollowTimeFormatted: data.nextFollowTime ? formatDateTime(data.nextFollowTime) : (row.nextFollowTime ? formatDateTime(row.nextFollowTime) : ''),
      followAddress: data.followAddress || '',
      images
    }
  } catch (error) {
    console.error('查看跟进详情失败:', error)
    detailDialogVisible.value = false
    ElMessage.error(error.message || '加载跟进详情失败')
  } finally {
    detailLoading.value = false
  }
}

// 编辑跟进
const handleEdit = async (row) => {
  try {
    loading.value = true
    const res = await getFollowDetail(row.id)
    if (res.code !== '000000' || !res.data) {
      throw new Error(res.msg || '获取跟进详情失败')
    }
    const data = res.data

    isEdit.value = true

    // 解析图片字段（后端存的是 JSON 字符串）
    let images = []
    if (data.images) {
      try {
        const parsed = JSON.parse(data.images)
        if (Array.isArray(parsed)) {
          images = parsed
        } else if (typeof parsed === 'string') {
          images = [parsed]
        }
      } catch {
        if (typeof data.images === 'string') {
          images = data.images.split(',').map(s => s.trim()).filter(Boolean)
        }
      }
    }

    // 确保图片 URL 为完整地址
    const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
    images = images.map(img => {
      if (!img) return ''
      if (img.startsWith('http://') || img.startsWith('https://')) return img
      return img.startsWith('/') ? baseURL + img : `${baseURL}/${img}`
    }).filter(Boolean)

    Object.assign(followForm, {
      id: data.id,
      customerId: data.customerId,
      customerName: data.customerName || row.customerName || '',
      activityType: data.activityType || 'visit',
      activityContent: data.activityContent || '',
      nextFollowTime: data.nextFollowTime ? data.nextFollowTime.slice(0, 10) : '',
      followAddress: data.followAddress || '',
      latitude: data.latitude || null,
      longitude: data.longitude || null,
      images
    })

    // 预加载客户选项，确保下拉显示名称
    customerOptions.value = [
      {
        id: followForm.customerId,
        customerName: followForm.customerName || '未命名客户'
      }
    ]

    editDialogVisible.value = true
  } catch (error) {
    console.error('编辑跟进失败:', error)
    ElMessage.error(error.message || '加载跟进详情失败')
  } finally {
    loading.value = false
  }
}

// 提交新增/编辑
const submitFollow = () => {
  if (!followFormRef.value) return

  followFormRef.value.validate(async (valid) => {
    if (!valid) return

  submitLoading.value = true
  submitting.value = true
  try {
    // 上传所有待上传的图片
    const pendingImages = followForm.images.filter(img => 
      img && typeof img === 'object' && img.status === 'pending' && img.file
    )
    
    if (pendingImages.length > 0) {
      ElMessage.info(`正在上传 ${pendingImages.length} 张图片...`)
      
      try {
        const uploadedImages = await batchUploadFiles(
          pendingImages,
          'FOLLOW',
          '客户跟进'
        )
        
        // 更新图片列表：将待上传的图片替换为已上传的URL
        const uploadedUrls = []
        for (let i = 0; i < followForm.images.length; i++) {
          const image = followForm.images[i]
          if (image && typeof image === 'object' && image.status === 'pending' && image.file) {
            const uploaded = uploadedImages.find(u => {
              return u.fileName === (image.fileName || image.file.name)
            })
            if (uploaded) {
              let imageUrl = uploaded.url
              if (!imageUrl.startsWith('http')) {
                const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
                imageUrl = imageUrl.startsWith('/') ? baseURL + imageUrl : `${baseURL}/${imageUrl}`
              }
              // 释放临时预览URL
              if (image.url && image.url.startsWith('blob:')) {
                URL.revokeObjectURL(image.url)
              }
              uploadedUrls.push(imageUrl)
            }
          } else {
            // 已经是URL字符串或已上传的对象
            uploadedUrls.push(typeof image === 'string' ? image : (image.url || image))
          }
        }
        followForm.images = uploadedUrls
      } catch (error) {
        ElMessage.error(`图片上传失败: ${error.message}`)
        throw error
      }
    }
    
    const payload = {
      id: followForm.id,
      customerId: followForm.customerId,
      activityType: followForm.activityType,
      activityContent: followForm.activityContent,
      nextFollowTime: followForm.nextFollowTime || null,
      followAddress: followForm.followAddress || '',
      latitude: followForm.latitude || null,
      longitude: followForm.longitude || null,
      // 图片：始终传字符串化后的数组，空数组表示清空旧图片
      images: JSON.stringify(
        Array.isArray(followForm.images)
          ? followForm.images
          : []
      )
    }

      let res
      if (isEdit.value) {
        res = await updateFollow(payload)
      } else {
        res = await addFollow(payload)
      }

      if (res.code === '000000') {
        ElMessage.success(isEdit.value ? '更新成功' : '新增成功')
        editDialogVisible.value = false
        loadFollowList()
      } else {
        throw new Error(res.msg || '操作失败')
      }
    } catch (error) {
      console.error('提交跟进失败:', error)
      ElMessage.error(error.message || '提交失败')
    } finally {
      submitLoading.value = false
      submitting.value = false
    }
  })
}

const handleSizeChange = (val) => {
  queryParams.pageSize = val
  queryParams.page = 1
  loadFollowList()
}

const handlePageChange = (val) => {
  queryParams.page = val
  loadFollowList()
}

// 导出跟进列表
const handleExport = async () => {
  // 限流检查：如果正在导出，直接返回
  if (exportLoading.value) {
    return
  }

  // 限流检查：如果距离上次导出时间太短，提示用户
  const now = Date.now()
  const timeSinceLastExport = now - lastExportTime.value
  if (lastExportTime.value > 0 && timeSinceLastExport < EXPORT_THROTTLE_INTERVAL) {
    const remainingSeconds = Math.ceil((EXPORT_THROTTLE_INTERVAL - timeSinceLastExport) / 1000)
    ElMessage.warning(`导出操作过于频繁，请等待 ${remainingSeconds} 秒后再试`)
    return
  }
  
  try {
    exportLoading.value = true
    
    const exportParams = {
      customerId: queryParams.customerId,
      customerName: queryParams.customerName,
      activityType: queryParams.activityType,
      activityContent: queryParams.activityContent,
      startDate: queryParams.startDate,
      endDate: queryParams.endDate,
      nextFollowStartDate: queryParams.nextFollowStartDate,
      nextFollowEndDate: queryParams.nextFollowEndDate
    }
    
    const blob = await exportFollowList(exportParams)
    
    if (!blob || blob.size === 0) {
      throw new Error('导出失败：返回数据格式错误')
    }
    
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `跟进列表_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}_${Date.now()}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('导出成功')
    // 导出成功后才记录时间戳，失败时不限流
    lastExportTime.value = Date.now()
  } catch (error) {
    console.error('导出失败:', error)
    const errorMsg = error.message || '导出失败'
    // 如果是限流错误（数据量过大），使用warning提示
    if (errorMsg.includes('数据量过大') || errorMsg.includes('超过最大限制')) {
      ElMessage.warning(errorMsg)
    } else {
      ElMessage.error(errorMsg)
    }
    // 导出失败时不更新时间戳，允许用户立即重试
  } finally {
    exportLoading.value = false
  }
}

// 打开定位选择器
const openLocationPicker = async () => {
  locationDialogVisible.value = true
  locationSearchKeyword.value = ''
  locationSearchResults.value = []
  selectedLocation.value = null

  await nextTick()
  initLocationMap()

  // 如果已有定位信息，在地图上显示
  if (followForm.latitude && followForm.longitude) {
    const location = {
      latitude: followForm.latitude,
      longitude: followForm.longitude,
      address: followForm.followAddress || ''
    }
    updateMapLocation(location)
    selectedLocation.value = location
  } else {
    // 尝试获取当前位置（静默失败，不影响地图使用）
    locationLoading.value = true
    getCurrentLocation()
      .then(async (currentLocation) => {
        try {
          const addressData = await reverseGeocode(currentLocation.longitude, currentLocation.latitude)
          
          const location = {
            latitude: currentLocation.latitude,
            longitude: currentLocation.longitude,
            address: addressData.address || ''
          }
          updateMapLocation(location)
          selectedLocation.value = location
          ElMessage.success('定位成功')
        } catch (geocodeError) {
          console.error('逆地理编码失败:', geocodeError)
          // 即使逆地理编码失败，也使用定位的经纬度
          const location = {
            latitude: currentLocation.latitude,
            longitude: currentLocation.longitude,
            address: ''
          }
          updateMapLocation(location)
          selectedLocation.value = location
          ElMessage.info('已定位，但获取地址失败，可手动编辑')
        }
      })
      .catch((error) => {
        // 定位失败不影响地图使用，只记录日志，不显示错误提示
        console.log('获取当前位置失败（不影响使用）:', error.message)
        // 不显示错误提示，让用户正常使用地图搜索和点击选择功能
      })
      .finally(() => {
        locationLoading.value = false
      })
  }
}

// 初始化地图
const initLocationMap = () => {
  const mapContainer = document.getElementById('location-map')
  if (!mapContainer || locationMap) return

  // 清空容器
  mapContainer.innerHTML = ''

  const tiandituKey = process.env.VUE_APP_TIANDITU_KEY || 'f1d9eced233438796aa8c612693d7a52'
  
  // 默认中心点（北京）
  const defaultLng = 116.397428
  const defaultLat = 39.90923
  const defaultZoom = 13
  
  // 如果有选中的位置，使用选中位置的经纬度
  let centerLng = defaultLng
  let centerLat = defaultLat
  if (selectedLocation.value && selectedLocation.value.longitude && selectedLocation.value.latitude) {
    centerLng = selectedLocation.value.longitude
    centerLat = selectedLocation.value.latitude
  }
  
  // 创建 Leaflet 地图
  const map = L.map(mapContainer, {
    center: [centerLat, centerLng],
    zoom: defaultZoom,
    zoomControl: true
  })
  
  // 添加天地图瓦片图层
  // 使用天地图矢量地图（vec_w）
  const tiandituVecLayer = L.tileLayer(
    `https://t{s}.tianditu.gov.cn/vec_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=vec&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILECOL={x}&TILEROW={y}&TILEMATRIX={z}&tk=${tiandituKey}`,
    {
      subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
      attribution: '&copy; 国家基础地理信息中心'
    }
  )
  
  // 添加天地图标注图层（cva_w）
  const tiandituAnnoLayer = L.tileLayer(
    `https://t{s}.tianditu.gov.cn/cva_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=cva&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILECOL={x}&TILEROW={y}&TILEMATRIX={z}&tk=${tiandituKey}`,
    {
      subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
      attribution: '&copy; 国家基础地理信息中心'
    }
  )
  
  // 添加图层到地图
  tiandituVecLayer.addTo(map)
  tiandituAnnoLayer.addTo(map)
  
  // 修复 Leaflet 默认图标路径问题
  delete L.Icon.Default.prototype._getIconUrl
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
  })
  
  // 创建标记
  let marker = null
  
  // 如果有选中的位置，添加标记
  if (selectedLocation.value && selectedLocation.value.latitude && selectedLocation.value.longitude) {
    marker = L.marker([selectedLocation.value.latitude, selectedLocation.value.longitude])
      .addTo(map)
  }
  
  // 地图点击事件（带限流）
  map.on('click', async (e) => {
    const { lat, lng } = e.latlng
    
    const now = Date.now()
    const timeSinceLastClick = now - lastMapClickTime.value
    
    // 检查是否在1秒限流间隔内
    if (lastMapClickTime.value > 0 && timeSinceLastClick < MAP_CLICK_THROTTLE_INTERVAL) {
      // 如果是首次被限流，记录首次被限流的时间
      if (firstThrottledClickTime.value === 0) {
        firstThrottledClickTime.value = now
      }
    }
    
    // 检查是否在5秒冷却时间内
    if (firstThrottledClickTime.value > 0) {
      const timeSinceFirstThrottled = now - firstThrottledClickTime.value
      const elapsedSeconds = Math.floor(timeSinceFirstThrottled / 1000)
      const remainingSeconds = MAP_CLICK_COOLDOWN_SECONDS - elapsedSeconds
      
      if (remainingSeconds > 0) {
        // 在冷却时间内，不进行选点和搜索，只显示提示
        ElMessage.warning(`操作频繁，请等待 ${remainingSeconds} 秒后再重试`)
        return
      } else {
        // 冷却时间已过，重置首次被限流时间
        firstThrottledClickTime.value = 0
      }
    }
    
    // 更新最后点击时间
    lastMapClickTime.value = now
    
    try {
      // 逆地理编码获取地址
      const addressData = await reverseGeocode(lng, lat)
      
      const location = {
        latitude: lat,
        longitude: lng,
        address: addressData.address || ''
      }
      
      // 更新选中位置
      selectedLocation.value = location
      
      // 更新标记
      if (marker) {
        marker.setLatLng([lat, lng])
      } else {
        marker = L.marker([lat, lng]).addTo(map)
      }
      
      // 地图移动到点击位置
      map.setView([lat, lng], map.getZoom())
      
      // 将地址信息回显到搜索框并执行搜索
      if (location.address) {
        // 更新搜索框内容
        locationSearchKeyword.value = location.address
        
        // 清除之前的防抖定时器
        if (searchDebounceTimer) {
          clearTimeout(searchDebounceTimer)
        }
        
        // 延迟执行搜索（防抖）
        searchDebounceTimer = setTimeout(() => {
          handleLocationSearch()
        }, SEARCH_DEBOUNCE_DELAY)
        
        ElMessage.success('位置已选择，正在搜索附近地点...')
      } else {
        ElMessage.info('位置已选择，但未获取到详细地址，可手动输入或使用搜索功能')
      }
    } catch (error) {
      console.error('获取地址失败:', error)
      // 即使获取地址失败，也允许选择位置
      const location = {
        latitude: lat,
        longitude: lng,
        address: ''
      }
      selectedLocation.value = location
      
      if (marker) {
        marker.setLatLng([lat, lng])
      } else {
        marker = L.marker([lat, lng]).addTo(map)
      }
      
      map.setView([lat, lng], map.getZoom())
      
      // 根据错误类型显示不同的提示
      let errorMsg = '位置已选择，但获取地址失败'
      if (error.message) {
        if (error.message.includes('API Key')) {
          errorMsg = '位置已选择，但API密钥配置有误，请检查天地图API Key配置'
        } else if (error.message.includes('网络') || error.message.includes('请求失败')) {
          errorMsg = '位置已选择，但网络请求失败，请检查网络连接或稍后重试'
        } else {
          errorMsg = `位置已选择，但获取地址失败：${error.message}`
        }
      }
      ElMessage.warning(errorMsg)
    }
  })
  
  locationMap = map
  locationMarker = marker
}

// 更新地图位置
const updateMapLocation = (location) => {
  if (!location || !location.latitude || !location.longitude) return
  if (!locationMap) return
  
  selectedLocation.value = location
  
  // 更新地图中心点
  locationMap.setView([location.latitude, location.longitude], 15)
  
  // 更新或创建标记
  if (locationMarker) {
    locationMarker.setLatLng([location.latitude, location.longitude])
  } else {
    locationMarker = L.marker([location.latitude, location.longitude]).addTo(locationMap)
  }
}

// 搜索地点
const handleLocationSearch = async () => {
  if (!locationSearchKeyword.value.trim()) {
    ElMessage.warning('请输入搜索关键词')
    return
  }

  locationSearchLoading.value = true
  try {
    const results = await searchLocation(locationSearchKeyword.value.trim(), {
      count: 10
    })
    locationSearchResults.value = results
    if (results.length === 0) {
      ElMessage.info('未找到相关地点')
    }
  } catch (error) {
    console.error('搜索地点失败:', error)
    ElMessage.error(error.message || '搜索失败')
  } finally {
    locationSearchLoading.value = false
  }
}

// 选择搜索结果
const selectLocationResult = (item) => {
  selectedLocation.value = {
    latitude: item.latitude,
    longitude: item.longitude,
    address: item.fullAddress
  }
  // 将选中的地址回显到搜索框
  locationSearchKeyword.value = item.fullAddress
  // 更新地图位置（不自动执行搜索）
  updateMapLocation(selectedLocation.value)
}

// 确认选择的位置
const confirmLocation = () => {
  if (!selectedLocation.value) {
    ElMessage.warning('请先选择位置')
    return
  }

  followForm.latitude = selectedLocation.value.latitude
  followForm.longitude = selectedLocation.value.longitude
  followForm.followAddress = selectedLocation.value.address || ''
  
  locationDialogVisible.value = false
  ElMessage.success('位置已设置')
}

// 定位对话框关闭时清理资源
const handleLocationDialogClose = () => {
  if (locationMap) {
    locationMap.remove()
    locationMap = null
    locationMarker = null
  }
  locationSearchKeyword.value = ''
  locationSearchResults.value = []
  selectedLocation.value = null
  lastMapClickTime.value = 0
  firstThrottledClickTime.value = 0
  // 清除搜索防抖定时器
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer)
    searchDebounceTimer = null
  }
}

// 清理地图资源
onBeforeUnmount(() => {
  if (locationMap) {
    locationMap.remove()
    locationMap = null
    locationMarker = null
  }
  // 清除搜索防抖定时器
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer)
    searchDebounceTimer = null
  }
})

onMounted(() => {
  loadFollowList()
})
</script>

<style scoped>
.follow-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
  /* 再向左偏移一些，使筛选条件与表格左侧位置更贴近（参考合同管理） */
  margin-left: -30px;
}

.search-form :deep(.el-form-item) {
  margin-bottom: 18px;
}

.action-buttons-inline {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

/* 下次跟进时间不换行 */
.next-follow-time-item :deep(.el-form-item__label) {
  white-space: nowrap;
  flex-shrink: 0;
}

.next-follow-time-item :deep(.el-form-item__content) {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
}

/* 下次跟进时间的日期选择框宽度与跟进时间保持一致 */
.next-follow-time-item :deep(.el-date-editor) {
  width: 100%;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}


.image-uploader {
  width: 100px;
  height: 100px;
}

.add-image-btn {
  width: 100px;
  height: 100px;
  border: 1px dashed #d9d9d9;
  background-color: #fafafa;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
}

.add-image-btn:hover {
  border-color: #409eff;
  background-color: #f0f9ff;
}

.add-image-btn .add-icon {
  font-size: 28px;
  color: #8c8c8c;
  transition: color 0.3s;
}

.add-image-btn:hover .add-icon {
  color: #409eff;
}

.upload-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.8;
}

.upload-tip .tip-item {
  margin: 2px 0;
  font-size: 12px;
  color: #999;
}

/* 跟进类型下拉框样式 */
:deep(.follow-type-select-dropdown) {
  min-width: 100px !important;
}

/* 表单中"下次跟进时间"等标签不换行 */
:deep(.no-wrap-label .el-form-item__label) {
  white-space: nowrap;
}

/* 定位相关样式 */
.location-input-wrapper {
  width: 100%;
}

.location-info {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #666;
}

.location-coords {
  color: #999;
}

.location-picker {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.location-search {
  width: 100%;
}

.location-results {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 8px;
}

.location-result-item {
  padding: 12px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.location-result-item:hover {
  background-color: #f5f7fa;
}

.result-name {
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
}

.result-address {
  font-size: 12px;
  color: #909399;
}

.location-map-container {
  position: relative;
  width: 100%;
  height: 400px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}

.location-map {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  z-index: 0;
}

/* 隐藏地图占位符，地图会直接显示 */
.map-placeholder {
  display: none;
}

.map-tip {
  position: absolute;
  bottom: 8px;
  right: 8px;
  font-size: 12px;
  color: #909399;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 4px 8px;
  border-radius: 4px;
}

.selected-location-info {
  margin-top: 8px;
}
</style>

